import { useQuery } from '@tanstack/react-query';
import { formatGuestSignInId } from '@kaeliux/shared';
import { getGuestSignInId } from '../../services/auth.service';
import { useAuthStore } from '../../store/authStore';
import { useToast } from '../../contexts/ToastContext';
import { Button } from '../ui/Button';
import { Icon } from '../ui/Icon';
import { Spinner } from '../ui/Spinner';

/**
 * Shows a guest their guest ID (issue #332): typed on the sign-in page, it
 * gets them back into this guest game on any device. The server allocates it
 * on first request and retires it once the account is registered.
 */
export default function GuestSignInIdPanel() {
    const uid = useAuthStore((s) => s.session?.uid);
    const { show } = useToast();
    const query = useQuery({
        queryKey: ['guestSignInId', uid],
        queryFn: getGuestSignInId,
        enabled: !!uid,
        staleTime: Infinity,
    });
    const guestId = query.data ? formatGuestSignInId(query.data) : null;

    const copy = async () => {
        if (!guestId) return;
        try {
            await navigator.clipboard.writeText(guestId);
            show('Guest ID copied.', { tone: 'success' });
        } catch {
            show("Couldn't copy the guest ID. Select it and copy it yourself.", { tone: 'warning' });
        }
    };

    return (
        <section className="flex flex-col gap-2" data-testid="guest-sign-in-id">
            <h4 className="text-content-muted text-label font-medium">Guest ID</h4>
            <p className="text-body text-content-secondary">
                Sign back in to this guest game with this ID from the sign-in page, on this device or any other.
            </p>
            {query.isPending ? (
                <span className="inline-flex items-center gap-2 text-body text-content-muted">
                    <Spinner className="h-4 w-4" /> Getting your guest ID
                </span>
            ) : query.isError ? (
                <p className="text-body text-status-danger" role="alert">
                    Your guest ID couldn&apos;t be loaded. Please try again later.
                </p>
            ) : (
                <div className="flex flex-wrap items-center gap-2">
                    <code
                        className="select-all rounded-control border border-hairline-strong bg-space-950 px-3 py-1.5 font-mono text-body text-content-primary"
                        data-testid="guest-sign-in-id-value"
                    >
                        {guestId}
                    </code>
                    <Button type="button" size="sm" variant="secondary" onClick={copy}>
                        Copy
                    </Button>
                </div>
            )}
            <p className="flex items-start gap-2 text-body text-status-warning" data-testid="guest-sign-in-id-warning">
                <Icon name="alert" size={16} className="mt-0.5 shrink-0" />
                <span>
                    Anyone with this ID can sign in to your guest account, so keep it private. It stops working once you
                    create an account.
                </span>
            </p>
        </section>
    );
}
